import { useAppSelector } from 'hooks/useAppSelector';
import { ComponentProps, useState } from 'react';
import { AssetTypePicker } from 'routes/essential-workspace/components/product-selector/asset-type-picker';
import { Catalog } from 'routes/essential-workspace/components/product-selector/catalog';
import { useInitEssentialForm } from 'routes/essential-workspace/components/product-selector/hooks/use-init-form';
import { useSearchInput } from 'routes/essential-workspace/components/product-selector/hooks/use-search-input';
import { MostRecentProducts } from 'routes/essential-workspace/components/product-selector/most-recent';
import { ProductList } from 'routes/essential-workspace/components/product-selector/product-list';
import { ProductOverlay } from 'routes/essential-workspace/components/product-selector/product-overlay';
import { SearchInput } from 'routes/essential-workspace/components/product-selector/search-input';
import { selectEssentialProductsWithPinned } from 'routes/essential-workspace/state/form.selectors';

export type EssentialProductSelectorProps = Omit<ComponentProps<'div'>, 'children'>;

export function EssentialProductSelector(props: EssentialProductSelectorProps) {
  const products = useAppSelector(selectEssentialProductsWithPinned);
  const [selectedAssetType, setSelectedAssetType] = useState<string>();
  const { inputRef, search, handleChange, clear } = useSearchInput();
  const { initEssentialForm } = useInitEssentialForm();

  const [opened, setOpened] = useState(false);

  const handleSelectAssetType = (assetType: string) => {
    setSelectedAssetType(assetType);
    setOpened(true);
  };

  const handleCloseOverlay = () => {
    setOpened(false);
    setSelectedAssetType(undefined);
    clear();
  };

  const isSearching = search.length > 0;

  return (
    <section {...props} aria-label="Product selector">
      <h1 className="h2 mb-3">Create new quote</h1>

      <SearchInput ref={inputRef} value={search} onChange={handleChange} onClick={() => setOpened(true)} />

      <div className="container-md px-0 px-md-5 flex-y gap-2 mt-2">
        <AssetTypePicker products={products} onSelectAssetType={handleSelectAssetType} />
        <MostRecentProducts products={products} onSelectProduct={initEssentialForm} />
      </div>

      <ProductOverlay
        target={inputRef.current}
        show={opened}
        className={isSearching ? 'h-auto' : undefined}
        onHide={handleCloseOverlay}
      >
        {isSearching ? (
          <ProductList search={search} products={products} onSelectProduct={initEssentialForm} />
        ) : (
          <Catalog products={products} onSelectProduct={initEssentialForm} initialAssetType={selectedAssetType} />
        )}
      </ProductOverlay>
    </section>
  );
}
